// src/App.jsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

import Navbar from './components/Navbar';
import Footer from './components/Footer';

import HomePage            from './pages/HomePage';
import CategoryPage        from './pages/CategoryPage';
import VendorPage          from './pages/VendorPage';
import ShopPage            from './pages/ShopPage';
import CartPage            from './pages/CartPage';
import CheckoutPage        from './pages/CheckoutPage';
import { LoginPage, RegisterPage } from './pages/AuthPage';
import AccountPage         from './pages/AccountPage';
import OrdersPage          from './pages/OrdersPage';
import WishlistPage        from './pages/WishlistPage';
import AdminPage           from './pages/AdminPage';
import ProductDetailPage   from './pages/ProductDetailPage';
import PrivacyPolicy   from './pages/PrivacyPolicy';


import './styles/global.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, userData, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (userData && userData.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function PageLoader() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid var(--border)', borderTopColor: 'var(--gold)', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Loading</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const AUTH_PATHS = ['/login', '/register'];

function Layout({ children }) {
  const location = useLocation();
  const isAuth = AUTH_PATHS.includes(location.pathname);
  if (isAuth) return <>{children}</>;
  return (
    <>
      <Navbar />
      <main style={{ minHeight: '80vh' }}>{children}</main>
      <Footer />
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <Layout>
            <Routes>
              {/* Public */}
              <Route path="/"                  element={<HomePage />} />
              <Route path="/category/:categoryId" element={<CategoryPage />} />
              <Route path="/vendor/:vendorId"  element={<VendorPage />} />
              <Route path="/shop"              element={<ShopPage />} />
              <Route path="/product/:id"      element={<ProductDetailPage />} />
              <Route path="/cart"              element={<CartPage />} />
              <Route path="/login"             element={<LoginPage />} />
              <Route path="/register"          element={<RegisterPage />} />

              {/* Protected */}
              <Route path="/checkout"  element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
              <Route path="/orders"    element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
              <Route path="/wishlist"  element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
              <Route path="/account"   element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/admin"     element={<AdminRoute><AdminPage /></AdminRoute>} />

              <Route path="*"          element={<Navigate to="/" replace />} />
                <Route path="/PrivacyPolicy"          element={<PrivacyPolicy />} />
            </Routes>
          </Layout>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
